/**
 * Elenco società (V54), vista di ingresso del Super Admin.
 *
 * Tutte le società con stato, Admin Squadra, consumo dei limiti e data di
 * creazione. Una società nuova nasce con l'invito del suo Admin Squadra, che
 * sceglie la password dal link e da lì la prepara: senza, nessuno potrebbe
 * entrarci. Disattivare fa uscire tutti alla richiesta successiva, e si
 * riattiva senza perdere nulla.
 */

import { type FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { FlaskConicalIcon, OctagonXIcon, PowerIcon, PowerOffIcon } from 'lucide-react';
import { creaSocietaSchema } from '@calcio/shared';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AzioniTestata,
  ComandoIcona,
  MenuAzioni,
  PulsanteInvio,
  PulsanteNuovo,
} from '../comuni/Azioni.js';
import { chiama } from '../api/client.js';
import { StatoCaricamento, Testata } from '../comuni/Testata.js';
import { Corpo } from '../comuni/Corpo.js';
import { erroriPerCampo, messaggio, useDati } from '../comuni/dati.js';
import { statoInvito } from '../amministrazione/ruoli.js';
import {
  type SocietaInElenco,
  eDemo,
  etichettaAccessoDemo,
  giorno,
  pieno,
  rapporto,
  type EsitoSocietaDemo,
} from './piattaforma.js';
import { useConferma } from '../comuni/Conferma.js';

const CONSUMI = [
  ['squadreStagione', 'Squadre'],
  ['giocatori', 'Anagrafiche'],
  ['account', 'Account'],
  ['assistente', 'Assistente nel mese'],
] as const;

export function ElencoSocieta() {
  const { dati, errore, ricarica } = useDati<SocietaInElenco[]>('/piattaforma/societa');
  const [nuova, setNuova] = useState(false);
  const [inCorso, setInCorso] = useState<string | null>(null);
  const [credenziali, setCredenziali] = useState<EsitoSocietaDemo | null>(null);
  const [chiedi, conferma] = useConferma();

  if (!dati)
    return (
      <Corpo>
        <StatoCaricamento errore={errore} />
      </Corpo>
    );

  const haDemo = dati.some(eDemo);

  const creaDemo = async () => {
    const si = await chiedi({
      titolo: 'Creare la società demo?',
      testo:
        'Demo SSD con Prima Squadra, Juniores e Pulcini, due stagioni e dati di prova. Si può rifare solo dopo aver svuotato il database.',
      azione: 'Crea',
    });
    if (!si) return;
    setInCorso('demo');
    try {
      const esito = await chiama<EsitoSocietaDemo>('/piattaforma/societa/demo', { metodo: 'POST' });
      await ricarica();
      setCredenziali(esito);
    } catch (e) {
      toast.error(messaggio(e));
    } finally {
      setInCorso(null);
    }
  };

  const impostaStato = async (s: SocietaInElenco) => {
    const si = await chiedi(
      s.attiva
        ? {
            titolo: `Disattivare ${s.nome}?`,
            testo: 'Chi è dentro esce subito e nessuno può più accedere finché non la riattivi.',
            azione: 'Disattiva',
            distruttiva: true,
          }
        : {
            titolo: `Riattivare ${s.nome}?`,
            testo: 'Account e inviti tornano validi.',
            azione: 'Riattiva',
          },
    );
    if (!si) return;
    setInCorso(s.id);
    try {
      await chiama(`/piattaforma/societa/${s.id}/stato`, {
        metodo: 'PUT',
        corpo: { attiva: !s.attiva },
      });
      await ricarica();
      toast.success(s.attiva ? `${s.nome} disattivata.` : `${s.nome} riattivata.`);
    } catch (e) {
      toast.error(messaggio(e));
    } finally {
      setInCorso(null);
    }
  };

  return (
    <>
      <Testata
        titolo="Società"
        voce="piattaforma-societa"
        azione={
          <AzioniTestata>
            <PulsanteNuovo etichetta="Nuova società" onClick={() => setNuova(true)} />
            <MenuAzioni
              voci={[
                {
                  etichetta: 'Crea società demo',
                  Icona: FlaskConicalIcon,
                  disabled: haDemo || inCorso !== null,
                  motivo: haDemo ? 'La demo esiste già.' : undefined,
                  onSelect: () => void creaDemo(),
                },
              ]}
            />
          </AzioniTestata>
        }
      />
      <Corpo>
        <div className="flex flex-col gap-4">
          {dati.length === 0 ? (
            <Empty>
              <EmptyHeader>
                <EmptyTitle>Nessuna società</EmptyTitle>
                <EmptyDescription>
                  Crea la prima società: il suo Admin Squadra riceve l'invito via email.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Società</TableHead>
                  <TableHead>Admin Squadra</TableHead>
                  <TableHead>Consumo</TableHead>
                  <TableHead>Creata il</TableHead>
                  <TableHead>
                    <span className="sr-only">Azioni</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dati.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell>
                      <Link
                        to={`/piattaforma/societa/${s.id}`}
                        className="flex flex-col items-start gap-1 font-medium hover:underline"
                      >
                        {s.nome}
                        <span className="flex flex-wrap gap-1">
                          {eDemo(s) && <Badge variant="outline">Demo</Badge>}
                          {!s.attiva && <Badge variant="secondary">Disattivata</Badge>}
                        </span>
                      </Link>
                    </TableCell>
                    <TableCell className="whitespace-normal">
                      {s.admin ? (
                        <span className="flex flex-col">
                          <span className="flex items-center gap-2">
                            {s.admin.nome} {s.admin.cognome}
                            {s.admin.invito && (
                              <Badge variant={s.admin.invito.scaduto ? 'destructive' : 'outline'}>
                                {s.admin.invito.scaduto ? 'Invito scaduto' : 'Invitato'}
                              </Badge>
                            )}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {s.admin.invito ? statoInvito(s.admin.invito) : s.admin.email}
                          </span>
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Nessun Admin</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs tabular-nums">
                      {CONSUMI.map(([k, etichetta]) => (
                        <span
                          key={k}
                          className={pieno(s.limiti[k]) ? 'block font-semibold' : 'block'}
                        >
                          {etichetta} {rapporto(s.limiti[k])}
                        </span>
                      ))}
                    </TableCell>
                    <TableCell className="tabular-nums">{giorno(s.creataIl)}</TableCell>
                    <TableCell>
                      <div className="flex justify-end">
                        <ComandoIcona
                          etichetta={`${s.attiva ? 'Disattiva' : 'Riattiva'} ${s.nome}`}
                          Icona={s.attiva ? PowerOffIcon : PowerIcon}
                          size="icon-sm"
                          disabled={inCorso === s.id}
                          onClick={() => void impostaStato(s)}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <Dialog open={nuova} onOpenChange={setNuova}>
          <DialogContent>
            {nuova && (
              <FormSocieta
                onChiudi={() => setNuova(false)}
                onCreata={async (email) => {
                  setNuova(false);
                  await ricarica();
                  toast.success('Società creata', {
                    description: `L'invito è partito verso ${email}.`,
                  });
                }}
              />
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={credenziali !== null} onOpenChange={(o) => !o && setCredenziali(null)}>
          <DialogContent>
            {credenziali && (
              <>
                <DialogHeader>
                  <DialogTitle>{credenziali.nome} è pronta</DialogTitle>
                  <DialogDescription>
                    Password unica per tutti gli account: {credenziali.accessi[0]?.password}. Entra
                    come Mister o Dirigente di una squadra per usare i dati di prova.
                  </DialogDescription>
                </DialogHeader>
                <ul className="flex flex-col gap-2 text-sm">
                  {credenziali.accessi.map((a) => (
                    <li key={a.email} className="flex flex-col">
                      <span className="font-medium">{etichettaAccessoDemo(a)}</span>
                      <span className="text-muted-foreground">{a.email}</span>
                    </li>
                  ))}
                </ul>
                <DialogFooter>
                  <Button onClick={() => setCredenziali(null)}>Ho annotato gli accessi</Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
        {conferma}
      </Corpo>
    </>
  );
}

function FormSocieta({
  onChiudi,
  onCreata,
}: {
  onChiudi: () => void;
  onCreata: (email: string) => Promise<void>;
}) {
  const [campi, setCampi] = useState({ societa: '', email: '', nome: '', cognome: '' });
  const [errori, setErrori] = useState<Record<string, string>>({});
  const [erroreServer, setErroreServer] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  const invia = async (e: FormEvent) => {
    e.preventDefault();
    const esito = creaSocietaSchema.safeParse(campi);
    if (!esito.success) return setErrori(erroriPerCampo(esito.error));
    setErrori({});
    setErroreServer(null);
    setInCorso(true);
    try {
      await chiama('/piattaforma/societa', { metodo: 'POST', corpo: esito.data });
      await onCreata(esito.data.email);
    } catch (errore) {
      setErroreServer(messaggio(errore));
      setInCorso(false);
    }
  };

  const campo = (chiave: keyof typeof campi, etichetta: string, tipo = 'text') => (
    <Field data-invalid={Boolean(errori[chiave]) || undefined}>
      <FieldLabel htmlFor={`societa-${chiave}`}>{etichetta}</FieldLabel>
      <Input
        id={`societa-${chiave}`}
        type={tipo}
        value={campi[chiave]}
        aria-invalid={Boolean(errori[chiave]) || undefined}
        onChange={(e) => setCampi({ ...campi, [chiave]: e.target.value })}
      />
      {errori[chiave] && <FieldError>{errori[chiave]}</FieldError>}
    </Field>
  );

  return (
    <form onSubmit={(e) => void invia(e)} className="flex flex-col gap-6">
      <DialogHeader>
        <DialogTitle>Nuova società</DialogTitle>
        <DialogDescription>
          L'Admin Squadra riceve un invito valido 7 giorni: sceglie la password e prepara stagioni,
          squadre e account. Limiti predefiniti, modificabili dalla scheda.
        </DialogDescription>
      </DialogHeader>

      <FieldGroup>
        {campo('societa', 'Nome della società')}
        <FieldSet>
          <FieldLegend variant="label">Admin Squadra</FieldLegend>
          <FieldGroup>
            {campo('email', 'Email', 'email')}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {campo('nome', 'Nome')}
              {campo('cognome', 'Cognome')}
            </div>
          </FieldGroup>
        </FieldSet>
      </FieldGroup>

      {erroreServer && (
        <Alert variant="destructive">
          <OctagonXIcon />
          <AlertTitle>Salvataggio non riuscito</AlertTitle>
          <AlertDescription>
            {erroreServer}{' '}
            {erroreServer.includes('già registrato') && (
              <Link
                to={`/piattaforma/account?q=${encodeURIComponent(campi.email.trim())}`}
                className="underline"
              >
                Cerca l'account
              </Link>
            )}
          </AlertDescription>
        </Alert>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onChiudi}>
          Annulla
        </Button>
        <PulsanteInvio type="submit" inCorso={inCorso}>
          Crea e invita
        </PulsanteInvio>
      </DialogFooter>
    </form>
  );
}
